/**
 * AtlasInputModule — keyboard + pointer-lock mouse look.
 *
 * Writes camera-relative movement intent and yaw into the shared `atlas` state
 * (consumed by the avatar module's FixedSim prediction), and raises the place
 * (E) and world-toggle (Tab) signals. Pointer lock engages on canvas click.
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { footZoomStep } from '../lib/foot_camera.js';
import { MODE_WALK, MODE_SPRINT } from '../lib/combat_core.js';
import { makeSystem, Phase } from '../engine-compat.js';
import {
  padPoll, padSnap, padHeld, padPressed, padRepeat, padClearRepeats, padMove, padLook,
  padEverSeen, padUiStep, padRumble, padCaptureStep, padXrActive, padBindings,
  padCaptureActive, padLastCapture, padRumbleEnabled, padRumbleGain, padHasActuator,
  PAD_UI_STICK_THRESHOLD,
} from '../lib/gamepad.js';
import { topNavPanel, navTargets, navRects, pickNext, markFocus, scrollableOf, NAV_SCROLL_RATE } from '../lib/ui_nav.js';
import { keyIs, keyHeldIn, keyBindings, keyCaptureStep, keyCaptureActive } from '../lib/keybinds.js';
import {
  loadTouchLayout, saveTouchLayout, resetTouchLayout, controlRect, moveControl, resizeControl,
  addControl, removeControl, defaultTouchLayout, TOUCH_LABEL, type TouchControl, type TouchAction,
} from '../lib/touch_layout.js';

const MOUSE_SENS = 0.0022;
const DRAG_SENS = 0.0042; // drag-look (headset 2D / touch) — a bit slower than locked-mouse
/** Minimum ms between scroll-wheel weapon steps. */
const WHEEL_STEP_MS = 180;
/** Two presses of the same movement key inside this window = a dash (build-now wave 3). */
const DOUBLE_TAP_MS = 260;
/**
 * BROWSER-SHORTCUT CAPTURE (2026-09-12, "ctrl+w closes the tab in the browser
 * when the mouse is locked in the game"). Chromium/Edge never let a page
 * preventDefault its reserved shortcuts (Ctrl+W/T/N, Ctrl+Tab…); the ONE
 * sanctioned way is the Keyboard Lock API, which only takes effect while
 * the page is FULLSCREEN. So the canvas click that locks the pointer also
 * enters fullscreen and locks every key EXCEPT Escape: Esc keeps its native
 * job — it leaves fullscreen, which frees the pointer. `localStorage
 * atlas:capture-keys = 'off'` turns the fullscreen + lock off (the pointer
 * still locks); the preventable shortcuts below are blocked while locked
 * either way, and an accidental close while locked gets the browser's
 * leave-page prompt (the one thing a non-fullscreen page can still do).
 */
const CAPTURE_KEYS_LS = 'atlas:capture-keys';
/** Every KeyboardEvent.code the lock should own — all of them but Escape. */
const LOCKED_KEY_CODES: readonly string[] = (() => {
  const codes: string[] = [];
  for (let i = 0; i < 26; i++) codes.push(`Key${String.fromCharCode(65 + i)}`);
  for (let i = 0; i <= 9; i++) codes.push(`Digit${i}`, `Numpad${i}`);
  for (let i = 1; i <= 12; i++) codes.push(`F${i}`);
  codes.push('Tab', 'Space', 'Enter', 'Backspace', 'Delete', 'Insert', 'Home', 'End', 'PageUp', 'PageDown',
    'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight',
    'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight', 'ContextMenu', 'CapsLock', 'NumLock', 'ScrollLock',
    'Backquote', 'Minus', 'Equal', 'BracketLeft', 'BracketRight', 'Backslash', 'Semicolon', 'Quote', 'Comma',
    'Period', 'Slash', 'NumpadAdd', 'NumpadSubtract', 'NumpadMultiply', 'NumpadDivide', 'NumpadDecimal', 'NumpadEnter');
  return codes;
})();
/** Ctrl/⌘ + these are browser actions a page MAY cancel (save, find, print,
 *  bookmark, history, downloads, open, source, reload…); blocked while locked. */
const CTRL_SHORTCUTS_BLOCKED = new Set(['KeyS', 'KeyD', 'KeyF', 'KeyG', 'KeyH', 'KeyJ', 'KeyK', 'KeyL', 'KeyO', 'KeyP', 'KeyU', 'KeyE', 'KeyB', 'KeyR', 'KeyA', 'Minus', 'Equal', 'Digit0']);
/** Function keys a page may cancel: help, find-next, reload, address bar, caret browsing. */
const FN_KEYS_BLOCKED = new Set(['F1', 'F3', 'F5', 'F6', 'F7']);
/** Movement keys → (forward, strafe) in the input module's convention. */
/** The pad's stick-flick dash directions (forward, strafe). */
const DASH_DIRS: Readonly<Record<string, readonly [number, number]>> = {
  F: [1, 0], B: [-1, 0], L: [0, -1], R: [0, 1],
};

export class AtlasInputModule implements IModule {
  readonly name = 'AtlasInput';

  private keys = new Set<string>();
  private canvas!: HTMLCanvasElement;
  /** Aborts every DOM listener registered in init() (dispose used to leave them all live). */
  private readonly ac = new AbortController();
  private jumpButton: HTMLElement | null = null;
  private pitch = 0;
  private dragId = -1;
  /** Wheel debounce: one ring step per notch (a trackpad fires many small deltas). */
  private lastWheelMs = -Infinity;
  private dragX = 0;
  private dragY = 0;
  private joyF = 0;
  private joyS = 0;
  /** Left-stick movement, merged into the intent exactly like the touch stick. */
  private padF = 0;
  private padS = 0;
  // THE TOUR DRIVE (2026-09-17, the perf tour): a product-side movement intent
  // merged exactly like the pad stick — never a synthetic KeyboardEvent (the
  // standing law). `f`/`s` are a camera-relative stick, `turn` a yaw rate in
  // rad/s applied on the input tick so a walking leg sweeps its surroundings.
  private autoF = 0;
  private autoS = 0;
  private autoTurn = 0;
  /** Held-state mirrors so a pad button behaves like the key it stands in for. */
  private padSprint = false;
  private padGuard = false;
  /** Last frame's stick direction, for the pad's double-tap dash. */
  private padDashDir = '';
  private padDashMs = -Infinity;

  init(app: App): void {
    this.installLookLever();
    this.installGateLever();
    this.installPadLever();
    // THE ONE POLL. Everything else in the game reads the snapshot this leaves
    // behind, so every consumer sees one consistent frame and an edge fires
    // exactly once — the ride's FIXED tick can run several times per frame.
    // Input phase, before anything that consumes intent.
    app.world.addSystem(makeSystem('AtlasPadInput', Phase.Input, 5, (dt: number) => this.padTick(dt)));
    this.canvas = app.renderer.native.domElement as HTMLCanvasElement;
    // The remembered on-foot zoom (0 = first person).
    try { const v = Number(localStorage.getItem('atlas:cam-back')); if (Number.isFinite(v) && v >= 0) atlas.camBack = footZoomStep(v, 0) === v ? v : 0; } catch { /* fine */ }

    window.addEventListener('keydown', (e) => this.onKey(e, true), { signal: this.ac.signal });
    window.addEventListener('keyup', (e) => this.onKey(e, false), { signal: this.ac.signal });

    this.canvas.addEventListener('click', () => {
      // When the admin editor is in cursor-edit mode the user's OS cursor must
      // stay free so the TransformControls gizmo and numeric panel are clickable.
      // Re-locking would steal focus away from both.
      if (atlas.editorCursorMode) return;
      if (document.pointerLockElement !== this.canvas) this.lockPointer();
    }, { signal: this.ac.signal });
    // The keyboard lock follows the pointer lock; Esc leaves fullscreen (its
    // native job), which drops the pointer lock — both listeners release.
    document.addEventListener('pointerlockchange', () => {
      if (document.pointerLockElement === this.canvas) this.lockKeyboard();
      else this.unlockKeyboard();
    }, { signal: this.ac.signal });
    document.addEventListener('fullscreenchange', () => { if (!document.fullscreenElement) this.unlockKeyboard(); }, { signal: this.ac.signal });
    // A close/reload while the pointer is locked is almost never meant (the
    // Ctrl+W that reached the browser): ask first. Not locked → no prompt.
    window.addEventListener('beforeunload', (e) => {
      if (document.pointerLockElement === this.canvas) { e.preventDefault(); e.returnValue = ''; }
    }, { signal: this.ac.signal });
    document.addEventListener('mousemove', (e) => this.onMouse(e), { signal: this.ac.signal });

    // ── COMBAT (2026-09-05): a pointer-LOCKED left click attacks (the first
    // click only locks — lock is requested on 'click', after this mousedown,
    // so it never swings) and the wheel walks the weapon ring. Neither fires
    // in fly/edit/space/vehicle/XR, where the wheel is the camera zoom and a
    // click places, selects or steers.
    this.canvas.addEventListener('mousedown', (e) => {
      if (document.pointerLockElement !== this.canvas || !this.groundCombat()) return;
      if (e.button === 0) bus.emit('atlas:attack', { source: 'mouse' });
      // GUARD (build-now wave 4): the right button HELD raises the guard; the
      // avatar module mirrors it to the server (setGuard) — the rising edge is
      // the parry attempt, judged server-side against the scheduled blow.
      if (e.button === 2) atlas.guardHeld = true;
    }, { signal: this.ac.signal });
    window.addEventListener('mouseup', (e) => { if (e.button === 2) atlas.guardHeld = false; }, { signal: this.ac.signal });
    window.addEventListener('blur', () => { atlas.guardHeld = false; }, { signal: this.ac.signal });
    this.canvas.addEventListener('contextmenu', (e) => { if (document.pointerLockElement === this.canvas) e.preventDefault(); }, { signal: this.ac.signal });
    this.canvas.addEventListener('wheel', (e) => {
      if (!this.groundCombat()) return;
      e.preventDefault();
      // 2026-09-09 ("switch 1st/3rd person with the scroll wheel like most
      // games"): the plain wheel is the CAMERA ZOOM on foot — in to first
      // person, out to a third-person orbit (lib/foot_camera); the weapon ring
      // moved to Q / Shift+Q and stays on Alt+wheel. No pointer lock needed
      // (drag-look users zoom too).
      if (!e.altKey) {
        const next = footZoomStep(atlas.camBack, e.deltaY);
        if (next !== atlas.camBack) { atlas.camBack = next; try { localStorage.setItem('atlas:cam-back', String(next)); } catch { /* fine */ } }
        return;
      }
      if (document.pointerLockElement !== this.canvas) return;
      const now = performance.now();
      if (e.deltaY === 0 || now - this.lastWheelMs < WHEEL_STEP_MS) return; // one step per notch, not per pixel event
      this.lastWheelMs = now;
      bus.emit('atlas:weapon-cycle', { dir: e.deltaY > 0 ? 1 : -1 });
    }, { signal: this.ac.signal, passive: false });

    // ── DRAG-LOOK (the Quest 2D-browser report: a controller ray has no
    // pointer lock — dragging the view IS its mouse). Active only while a
    // pointer is held on the canvas and lock is NOT engaged, so desktop
    // click-to-lock behaviour is untouched.
    this.canvas.addEventListener('pointerdown', (e) => {
      if (document.pointerLockElement === this.canvas || atlas.editorCursorMode) return;
      this.dragId = e.pointerId;
      this.dragX = e.clientX;
      this.dragY = e.clientY;
    }, { signal: this.ac.signal });
    window.addEventListener('pointermove', (e) => {
      if (this.dragId !== e.pointerId || document.pointerLockElement === this.canvas) return;
      if (atlas.xrPresenting || atlas.spaceFlight) return;
      const dx = e.clientX - this.dragX;
      const dy = e.clientY - this.dragY;
      this.dragX = e.clientX;
      this.dragY = e.clientY;
      this.applyLook(dx * DRAG_SENS, dy * DRAG_SENS);
    }, { signal: this.ac.signal });
    const endDrag = (e: PointerEvent): void => { if (this.dragId === e.pointerId) this.dragId = -1; };
    window.addEventListener('pointerup', endDrag, { signal: this.ac.signal });
    window.addEventListener('pointercancel', endDrag, { signal: this.ac.signal });

    // ── ON-SCREEN MOVE STICK for pointer-only devices (headset 2D view,
    // touch). Desktop mouse/keyboard users never see it.
    const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
    const questish = /OculusBrowser|Quest|Pico|VR/i.test(navigator.userAgent);
    const forced = new URLSearchParams(location.search).has('touch'); // probe/desktop testing
    if (coarse || questish || forced || 'ontouchstart' in window) this.buildMoveStick();
  }

  /** Probe lever: set the camera pitch directly (radians, + = look up) — a
   *  headless page has no pointer lock, and the far-terrain screenshot must
   *  look DOWN from the sky-ship. */
  private installLookLever(): void {
    (window as unknown as Record<string, unknown>).__cameraLook = (pitch: number) => {
      this.pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, pitch));
      atlas.localPitch = this.pitch;
      const cam = atlas.localMesh?.children[0];
      if (cam) cam.rotation.x = this.pitch;
      return this.pitch;
    };
  }

  /** Shared look application (mouse deltas or drag deltas). */
  private applyLook(dx: number, dy: number): void {
    // RIDING: the mouse steers the WHEEL, not the hull (2026-09-16). Writing the
    // craft's yaw here rotated it INSTANTLY at any speed, so the yaw-rate
    // ceiling, the speed falloff, the drift turn-in and the self-aligning
    // torque all applied to the A/D keys alone — the mouse was a cheat code and
    // the two inputs had different physics. The delta is banked and the
    // vehicle's FIXED tick consumes it (deterministic for a replay layer).
    if (atlas.hoverRide) atlas.hoverMouseDx += dx;
    else atlas.localYaw -= dx;
    this.pitch -= dy;
    this.pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, this.pitch));
    atlas.localPitch = this.pitch; // the avatar module re-applies it + the feel offsets every frame
    if (atlas.localMesh) {
      const cam = atlas.localMesh.children[0];
      if (cam) cam.rotation.x = this.pitch;
    }
    this.updateIntent();
  }

  /**
   * The on-screen controls, built from the player's own LAYOUT (2026-09-16).
   * There are no keys to remap on a touchscreen; what a player needs instead is
   * to choose which controls exist, how big they are and where — a thumb that
   * cannot reach the jump button is the touch equivalent of a key bound to the
   * wrong finger. Positions are fractions of the viewport, so a layout arranged
   * in portrait survives a rotation.
   */
  private touchLayout: TouchControl[] = [];
  private touchRoot: HTMLElement | null = null;
  private touchArrange = false;

  private buildMoveStick(): void {
    const root = document.createElement('div');
    root.id = 'touch-controls';
    root.style.cssText = 'position:fixed;inset:0;z-index:41;pointer-events:none';
    document.body.appendChild(root);
    this.touchRoot = root;
    this.touchLayout = loadTouchLayout(window.innerWidth, window.innerHeight);
    this.renderTouch();
    window.addEventListener('resize', () => this.renderTouch(), { signal: this.ac.signal });
    (window as unknown as Record<string, unknown>).__touchLayout = () => ({
      arrange: this.touchArrange,
      controls: this.touchLayout.map((c) => ({ ...c })),
      arrangeMode: (on?: boolean) => { this.setTouchArrange(on ?? !this.touchArrange); return this.touchArrange; },
      add: (a: TouchAction) => { this.touchLayout = addControl(this.touchLayout, a, window.innerWidth, window.innerHeight); this.persistTouch(); return this.touchLayout.length; },
      remove: (id: string) => { this.touchLayout = removeControl(this.touchLayout, id); this.persistTouch(); return this.touchLayout.length; },
      reset: () => { resetTouchLayout(); this.touchLayout = defaultTouchLayout(); this.persistTouch(); return this.touchLayout.length; },
    });
  }

  /** Arrange mode: the controls become draggable and resizable instead of acting. */
  setTouchArrange(on: boolean): void {
    this.touchArrange = on;
    this.renderTouch();
  }
  touchArrangeOn(): boolean { return this.touchArrange; }
  touchControls(): readonly TouchControl[] { return this.touchLayout; }
  addTouchControl(a: TouchAction): void { this.touchLayout = addControl(this.touchLayout, a, window.innerWidth, window.innerHeight); this.persistTouch(); }
  removeTouchControl(id: string): void { this.touchLayout = removeControl(this.touchLayout, id); this.persistTouch(); }
  resetTouchControls(): void { resetTouchLayout(); this.touchLayout = defaultTouchLayout(); this.persistTouch(); }

  private persistTouch(): void {
    saveTouchLayout(this.touchLayout);
    this.renderTouch();
  }

  private renderTouch(): void {
    const root = this.touchRoot;
    if (!root) return;
    root.textContent = '';
    const vw = window.innerWidth, vh = window.innerHeight;
    for (const c of this.touchLayout) {
      const el = c.action === 'stick' ? this.buildStick(c) : this.buildTapButton(c);
      const r = controlRect(c, vw, vh);
      el.style.left = `${r.left}px`;
      el.style.top = `${r.top}px`;
      el.style.width = `${r.size}px`;
      el.style.height = `${r.size}px`;
      if (this.touchArrange) this.makeArrangeable(el, c);
      root.appendChild(el);
    }
    if (this.touchArrange) {
      const hint = document.createElement('div');
      hint.style.cssText =
        'position:absolute;left:50%;top:14px;transform:translateX(-50%);pointer-events:none;' +
        'background:rgba(9,13,20,.9);border:1px solid #35506a;border-radius:8px;padding:6px 12px;' +
        'color:#cde;font:12px system-ui';
      hint.textContent = 'Arranging — drag to move, pinch or drag the corner to resize';
      root.appendChild(hint);
    }
  }

  /** The left-thumb stick: the same intent WASD writes. */
  private buildStick(c: TouchControl): HTMLElement {
    const pad = document.createElement('div');
    pad.id = 'move-stick';
    pad.dataset.touchId = c.id;
    pad.style.cssText =
      'position:absolute;border-radius:50%;background:rgba(18,26,40,0.5);border:1px solid #2e4668;' +
      'touch-action:none;pointer-events:auto';
    const knob = document.createElement('div');
    knob.style.cssText =
      'position:absolute;left:50%;top:50%;width:40%;height:40%;border-radius:50%;transform:translate(-50%,-50%);' +
      'background:rgba(90,140,200,0.65);border:1px solid #6fa9e8;pointer-events:none';
    pad.appendChild(knob);
    if (this.touchArrange) return pad;
    let stickId = -1;
    const setVec = (e: PointerEvent): void => {
      const r = pad.getBoundingClientRect();
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      let vx = (e.clientX - cx) / (r.width / 2);
      let vy = (e.clientY - cy) / (r.height / 2);
      const len = Math.hypot(vx, vy);
      if (len > 1) { vx /= len; vy /= len; }
      this.joyS = vx;
      this.joyF = -vy;
      knob.style.transform = `translate(calc(-50% + ${vx * 30}%), calc(-50% + ${vy * 30}%))`;
      this.updateIntent();
    };
    pad.addEventListener('pointerdown', (e) => {
      stickId = e.pointerId;
      try { pad.setPointerCapture(e.pointerId); } catch { /* synthetic/odd pointers lack capture */ }
      setVec(e);
    }, { signal: this.ac.signal });
    pad.addEventListener('pointermove', (e) => { if (stickId === e.pointerId) setVec(e); }, { signal: this.ac.signal });
    const release = (e: PointerEvent): void => {
      if (stickId !== e.pointerId) return;
      stickId = -1;
      this.joyF = 0;
      this.joyS = 0;
      knob.style.transform = 'translate(-50%,-50%)';
      this.updateIntent();
    };
    pad.addEventListener('pointerup', release, { signal: this.ac.signal });
    pad.addEventListener('pointercancel', release, { signal: this.ac.signal });
    return pad;
  }

  /** A tap button. Every one runs the SAME path its key and its pad button do. */
  private buildTapButton(c: TouchControl): HTMLElement {
    const b = document.createElement('button');
    const meta = TOUCH_LABEL[c.action];
    b.textContent = meta.glyph;
    b.title = meta.name;
    b.dataset.touchId = c.id;
    b.dataset.touchAction = c.action;
    b.style.cssText =
      'position:absolute;border-radius:50%;background:rgba(18,26,40,0.6);color:#9cf;' +
      'border:1px solid #2e4668;font:700 20px system-ui;touch-action:none;pointer-events:auto;padding:0';
    if (c.action === 'jump') this.jumpButton = b;
    if (this.touchArrange) return b;
    const press = (): void => this.touchAct(c.action, true);
    const lift = (): void => this.touchAct(c.action, false);
    b.addEventListener('pointerdown', (e) => { e.preventDefault(); press(); }, { signal: this.ac.signal });
    b.addEventListener('pointerup', lift, { signal: this.ac.signal });
    b.addEventListener('pointercancel', lift, { signal: this.ac.signal });
    b.addEventListener('pointerleave', lift, { signal: this.ac.signal });
    return b;
  }

  /** ONE place a touch button turns into the game's own action. */
  private touchAct(action: TouchAction, down: boolean): void {
    switch (action) {
      case 'jump': if (down) atlas.intentJump = true; break;
      case 'sprint': this.touchSprint = down; this.updateIntent(); break;
      case 'crouch': if (down && !atlas.flyMode) atlas.intents.request(this.sprinting() ? 'slide' : 'crouch', atlas.fixedTick); break;
      case 'attack': if (down && this.groundCombat()) bus.emit('atlas:attack', { source: 'touch' }); break;
      case 'guard': atlas.guardHeld = down; break;
      case 'interact': if (down) bus.emit('atlas:place-requested', {}); break;
      case 'weaponNext': if (down && this.groundCombat()) bus.emit('atlas:weapon-cycle', { dir: 1 }); break;
      case 'map': if (down) bus.emit('atlas:toggle-world', {}); break;
      case 'inventory': if (down) bus.emit('atlas:open-workbench', { tab: 'inventory' }); break;
      case 'menu': if (down) bus.emit('atlas:toggle-options', {}); break;
      case 'stick': break;
    }
  }
  private touchSprint = false;

  /** Drag to move, drag the corner to resize — only while arranging. */
  private makeArrangeable(el: HTMLElement, c: TouchControl): void {
    el.style.pointerEvents = 'auto';
    el.style.outline = '2px dashed #7fb4e8';
    el.style.outlineOffset = '2px';
    let id = -1, lastX = 0, lastY = 0;
    el.addEventListener('pointerdown', (e) => {
      e.preventDefault(); e.stopPropagation();
      id = e.pointerId; lastX = e.clientX; lastY = e.clientY;
      try { el.setPointerCapture(e.pointerId); } catch { /* fine */ }
    }, { signal: this.ac.signal });
    el.addEventListener('pointermove', (e) => {
      if (id !== e.pointerId) return;
      const dx = e.clientX - lastX, dy = e.clientY - lastY;
      lastX = e.clientX; lastY = e.clientY;
      this.touchLayout = moveControl(this.touchLayout, c.id, dx, dy, window.innerWidth, window.innerHeight);
      const moved = this.touchLayout.find((x) => x.id === c.id)!;
      const r = controlRect(moved, window.innerWidth, window.innerHeight);
      el.style.left = `${r.left}px`; el.style.top = `${r.top}px`;
    }, { signal: this.ac.signal });
    const end = (e: PointerEvent): void => { if (id === e.pointerId) { id = -1; saveTouchLayout(this.touchLayout); } };
    el.addEventListener('pointerup', end, { signal: this.ac.signal });
    el.addEventListener('pointercancel', end, { signal: this.ac.signal });
    // A corner grip resizes, because pinching a 50 px button is not practical.
    const grip = document.createElement('div');
    grip.style.cssText =
      'position:absolute;right:-6px;bottom:-6px;width:20px;height:20px;border-radius:50%;' +
      'background:#7fb4e8;border:1px solid #cfe6ff;touch-action:none;cursor:nwse-resize';
    let gid = -1, gx = 0;
    grip.addEventListener('pointerdown', (e) => {
      e.preventDefault(); e.stopPropagation();
      gid = e.pointerId; gx = e.clientX;
      try { grip.setPointerCapture(e.pointerId); } catch { /* fine */ }
    }, { signal: this.ac.signal });
    grip.addEventListener('pointermove', (e) => {
      if (gid !== e.pointerId) return;
      const d = e.clientX - gx; gx = e.clientX;
      this.touchLayout = resizeControl(this.touchLayout, c.id, d * 2, window.innerWidth, window.innerHeight);
      this.renderTouch();
    }, { signal: this.ac.signal });
    const gend = (e: PointerEvent): void => { if (gid === e.pointerId) { gid = -1; saveTouchLayout(this.touchLayout); } };
    grip.addEventListener('pointerup', gend, { signal: this.ac.signal });
    grip.addEventListener('pointercancel', gend, { signal: this.ac.signal });
    el.appendChild(grip);
  }

  // ── Browser-shortcut capture (see CAPTURE_KEYS_LS) ───────────────────────
  private keyboardLocked = false;
  private captureKeys(): boolean {
    try { return localStorage.getItem(CAPTURE_KEYS_LS) !== 'off'; } catch { return true; }
  }
  /** Lock the pointer — through fullscreen first when capture is on, so the
   *  Keyboard Lock can own Ctrl+W & co. Both requests ride the click's user
   *  activation (the fullscreen promise resolves well inside it); a refused
   *  or unavailable fullscreen still locks the pointer plainly. */
  private lockPointer(): void {
    const plain = (): void => { try { this.canvas.requestPointerLock(); } catch { /* headset 2D view — drag-look covers it */ } };
    if (!this.captureKeys() || !document.fullscreenEnabled || document.fullscreenElement) { plain(); return; }
    let p: Promise<void> | undefined;
    try { p = document.documentElement.requestFullscreen({ navigationUI: 'hide' }); } catch { plain(); return; }
    p.then(plain, plain);
  }
  private lockKeyboard(): void {
    if (this.keyboardLocked || !this.captureKeys() || !document.fullscreenElement) return;
    const kb = (navigator as unknown as { keyboard?: { lock?(codes?: string[]): Promise<void> } }).keyboard;
    if (!kb?.lock) return;
    this.keyboardLocked = true;
    kb.lock(LOCKED_KEY_CODES as string[]).catch(() => { this.keyboardLocked = false; });
  }
  private unlockKeyboard(): void {
    if (!this.keyboardLocked) return;
    this.keyboardLocked = false;
    const kb = (navigator as unknown as { keyboard?: { unlock?(): void } }).keyboard;
    try { kb?.unlock?.(); } catch { /* not supported */ }
  }
  /** While the pointer is locked, cancel the browser actions a page is allowed
   *  to cancel (the reserved ones need the Keyboard Lock above). Esc is never
   *  touched. Alt alone would hand focus to the browser's menu bar. */
  private blockBrowserShortcut(e: KeyboardEvent): boolean {
    if (document.pointerLockElement !== this.canvas || e.code === 'Escape') return false;
    if ((e.ctrlKey || e.metaKey) && CTRL_SHORTCUTS_BLOCKED.has(e.code)) { e.preventDefault(); return true; }
    if (FN_KEYS_BLOCKED.has(e.code)) { e.preventDefault(); return true; }
    if (e.code === 'AltLeft' || e.code === 'AltRight') { e.preventDefault(); return false; }
    return false;
  }

  private onKey(e: KeyboardEvent, down: boolean): void {
    // Ignore typing in the identity kiosk inputs.
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
    // Browser shortcuts must not reach the browser while the game owns the
    // mouse (a blocked Ctrl+S is still a plain S for the game below).
    this.blockBrowserShortcut(e);

    const code = e.code;
    if (down) {
      // A REBIND IN PROGRESS SWALLOWS THE PRESS. Otherwise the very key being
      // captured would also do its old job on the way past — and Escape, which
      // cancels, would close the panel the player is rebinding from.
      if (!e.repeat && keyCaptureStep(code)) { e.preventDefault(); return; }
      this.keys.add(code);
      if (keyIs(code, 'interact')) bus.emit('atlas:place-requested', {});
      // The weapon ring; Shift walks it backwards, and `weaponPrev` may also
      // carry a key of its own.
      if (!e.repeat && this.groundCombat()) {
        if (keyIs(code, 'weaponNext')) bus.emit('atlas:weapon-cycle', { dir: e.shiftKey ? -1 : 1 });
        else if (keyIs(code, 'weaponPrev')) bus.emit('atlas:weapon-cycle', { dir: -1 });
        if (keyIs(code, 'attack')) bus.emit('atlas:attack', { source: 'key' });
      }
      if (keyIs(code, 'map')) {
        e.preventDefault();
        bus.emit('atlas:toggle-world', {});
      }
      // BUILD-NOW wave 1 (2026-09-12): every discrete press is a REQUEST in
      // the one intent queue (lib/intent_queue), stamped with the fixed tick
      // and consumed at most once by the avatar's fixed step; its TTL is
      // counted in fixed ticks (a wall-clock window measured from the tap
      // expired before any step saw it on a hitching frame — the slide
      // lesson). Held keys stay held state (Shift, the guard).
      if (keyIs(code, 'jump') && !e.repeat) atlas.intents.request('jump', atlas.fixedTick);
      // COMBAT WAVE movement grammar: Shift held = sprint; the crouch key
      // (left Ctrl) tapped WHILE sprinting = slide (BO6: "to slide … you must
      // be in a sprint"); tapped otherwise = CROUCH toggle (build-now wave 2).
      // The slide runs its SLIDE_MS on its own; releasing nothing matters —
      // the tap is the commitment. Never in edit/fly mode.
      // Not C: C is the Social Compass on the ground and countermeasures in
      // space (the probe found every C tap opening the compass window).
      if (keyIs(code, 'crouch') && !e.repeat && !atlas.flyMode) {
        atlas.intents.request(this.sprinting() ? 'slide' : 'crouch', atlas.fixedTick);
      }
      // DASH (build-now wave 3): a double-tap of a movement key inside
      // DOUBLE_TAP_MS — the direction of THAT key rides with the request.
      if (!e.repeat && this.groundCombat()) this.noteTapForDash(code);
    } else {
      this.keys.delete(code);
    }
    this.updateIntent();
  }

  /**
   * The pad, on foot and in the shell. Buttons fire the SAME requests, bus
   * events and held flags the keys do — never a synthetic KeyboardEvent (the
   * standing law, and a fake key would bypass every gate the real handlers
   * check). Blended per channel, so a pad idle in a USB port never fights the
   * keyboard and a player may use both at once.
   */
  /** Probe/tour drive: a stick the code holds (see `autoF`). Zeros release it. */
  setAutoWalk(f: number, s: number, turnRadPerS = 0): void {
    this.autoF = Math.max(-1, Math.min(1, f)); this.autoS = Math.max(-1, Math.min(1, s)); this.autoTurn = turnRadPerS;
    this.updateIntent();
  }
  get autoWalking(): boolean { return this.autoF !== 0 || this.autoS !== 0 || this.autoTurn !== 0; }

  private padTick(dt: number): void {
    if (this.autoTurn !== 0 && !atlas.hoverRide && !atlas.xrPresenting) { atlas.localYaw -= this.autoTurn * dt; this.updateIntent(); }
    const snap = padPoll(dt);
    if (!snap.present) {
      if (this.padF || this.padS || this.padSprint || this.padGuard) {
        this.padF = this.padS = 0; this.padSprint = false;
        if (this.padGuard) { this.padGuard = false; atlas.guardHeld = false; }
        this.updateIntent();
      }
      return;
    }
    // A REBIND IN PROGRESS OWNS THE FRAME. Without this the very press being
    // captured would also activate whatever control has focus behind the prompt.
    if (padCaptureStep()) { this.padReleaseHeld(); return; }

    // START is the only button that means the same thing everywhere: it opens
    // and closes the Options menu, including out of whatever panel is up.
    if (padPressed('menu')) bus.emit('atlas:toggle-options', {});

    // ── WINDOWS AND MENUS. A pad has no cursor, so with a panel open the pad
    //    drives FOCUS instead of the world: the d-pad (or the left stick) moves
    //    between controls, A presses, B closes, the shoulders change tab.
    //    THE CONTEXTS ARE EXCLUSIVE. The binding table lets an action share a
    //    button with one in another context, and that promise only holds if a
    //    frame belongs to exactly one context — d-pad right is Inventory in the
    //    world and "move focus right" in a panel, so it must never be both. ──
    if (this.padUiTick(dt)) { this.padReleaseHeld(); return; }

    // ── The shell, in the world only. ──
    if (padPressed('map')) bus.emit('atlas:toggle-world', {});
    if (padPressed('inventory')) bus.emit('atlas:open-workbench', { tab: 'inventory' });
    if (atlas.loadingGate) { this.padReleaseHeld(); return; }

    // ── Look. The right stick aims, on the same curve the steering uses. The
    //    ship and the sector steer themselves with it (as they do the mouse),
    //    and IN A HEADSET the player's neck does — never the stick. ──
    if (!atlas.spaceFlight && !atlas.skyFlight && !atlas.xrPresenting) {
      const { dx, dy } = padLook(dt);
      if (dx !== 0 || dy !== 0) this.applyLook(dx, dy);
    }

    // ── On foot only from here. ──
    // Edit mode flies its own camera and nudges its own gizmo with the pad, so
    // the world must not also read it — the exclusive-context rule again.
    const onFoot = !atlas.flyMode && !atlas.spaceFlight && !atlas.skyFlight && !atlas.hoverRide
      && !atlas.editorCursorMode && !atlas.editMode;
    if (!onFoot) { this.padReleaseHeld(); return; }

    // Movement: the left stick merges into the intent like the touch stick —
    // EXCEPT in a headset, where the XR locomotion system owns the intent
    // (comfort turning, teleport arcs, the standing frame) and a second writer
    // would fight it.
    if (!atlas.xrPresenting) {
      const mv = padMove();
      if (mv.f !== this.padF || mv.s !== this.padS) { this.padF = mv.f; this.padS = mv.s; this.updateIntent(); }
    } else if (this.padF || this.padS) { this.padF = this.padS = 0; this.updateIntent(); }

    // Sprint is a HELD state, exactly as Shift is.
    const sprint = padHeld('sprint');
    if (sprint !== this.padSprint) { this.padSprint = sprint; this.updateIntent(); }

    // Discrete requests go through the ONE intent queue, stamped with the fixed
    // tick, the same as the keys.
    if (padPressed('jump')) atlas.intents.request('jump', atlas.fixedTick);
    if (padPressed('crouch')) atlas.intents.request(this.sprinting() ? 'slide' : 'crouch', atlas.fixedTick);
    if (padPressed('interact')) bus.emit('atlas:place-requested', {});

    if (this.groundCombat()) {
      if (padPressed('attack')) { bus.emit('atlas:attack', { source: 'gamepad' }); padRumble('fire'); }
      if (padPressed('weaponNext')) bus.emit('atlas:weapon-cycle', { dir: 1 });
      if (padPressed('weaponPrev')) bus.emit('atlas:weapon-cycle', { dir: -1 });
      // The guard is held, and the RISING edge is the parry attempt — the
      // avatar module mirrors the flag to the server on its next step.
      const guard = padHeld('guard');
      if (guard !== this.padGuard) { this.padGuard = guard; atlas.guardHeld = guard; }
      // The camera zoom the wheel does, repeating while the d-pad is held.
      if (padRepeat('zoomIn', dt) || padRepeat('zoomOut', dt)) {
        const dir = padHeld('zoomIn') ? -1 : 1;
        const next = footZoomStep(atlas.camBack, dir * 120);
        if (next !== atlas.camBack) { atlas.camBack = next; try { localStorage.setItem('atlas:cam-back', String(next)); } catch { /* fine */ } }
      }
      this.padDash(dt);
    }
  }

  /**
   * Drive the topmost open panel with the pad. Returns true when it took the
   * pad, which is what stops the world reading it in the same frame.
   */
  private padUiTick(dt: number): boolean {
    const panel = topNavPanel();
    if (!panel) {
      if (atlas.padUiCapture) { markFocus(null, this.padFocusEl); this.padFocusEl = null; atlas.padUiCapture = false; padClearRepeats(); }
      return false;
    }
    if (!atlas.padUiCapture) { atlas.padUiCapture = true; padClearRepeats(); this.padFocusEl = null; }
    if (this.padPanel !== panel) { this.padPanel = panel; this.padFocusEl = null; }   // a new panel starts at its entry point

    const els = navTargets(panel);
    if (!els.length) return true;
    let idx = this.padFocusEl ? els.indexOf(this.padFocusEl) : -1;
    // A panel that re-renders on every change (the options menu) replaces the focused element; it hands the DOM
    // focus to the new copy of that control, so follow it rather than throwing the player back to the top.
    if (idx < 0 && this.padFocusEl && !this.padFocusEl.isConnected) {
      const a = document.activeElement as HTMLElement | null;
      const j = a ? els.indexOf(a) : -1;
      if (j >= 0) { idx = j; this.padFocusEl = a; markFocus(a, null); } else this.padFocusEl = null;
    }

    // B closes through the panel's OWN close button, so whatever teardown that
    // panel owns still runs. It is found by an explicit marker, never by its
    // label: matching the character '×' found "Ultra 1.3×" among the graphics
    // presets, and B set the render preset instead of closing the menu.
    if (padPressed('uiBack')) {
      const close = panel.querySelector<HTMLElement>('[data-atlas-close]');
      if (close) close.click();
      else panel.style.display = 'none';
      markFocus(null, this.padFocusEl); this.padFocusEl = null; this.padPanel = null;
      return true;
    }
    // The shoulders walk the tab strip, when the panel has one.
    if (padPressed('uiTabNext') || padPressed('uiTabPrev')) {
      // A panel that MARKS its tabs (`data-tab`) is walked by those alone, from the selected one. The label guess is
      // only for panels that mark nothing: in the options menu it also matched "Off", "On" and "Low" — and the
      // shoulders CLICKED them.
      const marked = els.filter((e) => e.dataset.tab !== undefined);
      const tabs = marked.length > 1 ? marked : els.filter((e) => e.dataset.tab !== undefined || /^\s*[A-Z][A-Za-z ]{1,14}\s*$/.test(e.textContent ?? ''));
      if (tabs.length > 1) {
        const selected = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
        const at = selected >= 0 ? selected : this.padFocusEl ? tabs.indexOf(this.padFocusEl) : -1;
        const step = padPressed('uiTabNext') ? 1 : -1;
        const next = tabs[(Math.max(0, at) + step + tabs.length) % tabs.length];
        next.click();
        this.padFocusEl = next; markFocus(next, null);
        return true;
      }
    }
    // THE RIGHT STICK SCROLLS. A long panel is unreachable by focus alone when
    // its controls run off the bottom, and a player expects the other stick to
    // pan — so it drives the scrollable region the focus sits in.
    const rs = padSnap();
    if (Math.abs(rs.ry) > 0.12 || Math.abs(rs.rx) > 0.12) {
      const sc = scrollableOf(this.padFocusEl ?? els[0] ?? null, panel);
      if (sc) {
        sc.scrollTop += rs.ry * NAV_SCROLL_RATE * dt;
        if (sc.scrollWidth - sc.clientWidth > 4) sc.scrollLeft += rs.rx * NAV_SCROLL_RATE * dt;
      }
    }

    // Directions move the focus; the first press only reveals it.
    const step = padUiStep(dt);
    if (step.x !== 0 || step.y !== 0) {
      const n = pickNext(navRects(els), idx, step.x, step.y);
      if (n >= 0) { idx = n; markFocus(els[idx], this.padFocusEl); this.padFocusEl = els[idx]; padRumble('ui'); }
    } else if (idx < 0 && (padPressed('uiAccept') || padSnap().present)) {
      const n = pickNext(navRects(els), -1, 0, 0);
      if (n >= 0 && this.padFocusEl === null) { idx = n; markFocus(els[idx], null); this.padFocusEl = els[idx]; }
    }
    if (padPressed('uiAccept') && idx >= 0) els[idx].click();
    return true;
  }
  private padFocusEl: HTMLElement | null = null;
  private padPanel: HTMLElement | null = null;

  /** Drop every held mirror the pad owns (leaving foot context, or losing it). */
  private padReleaseHeld(): void {
    if (this.padF || this.padS) { this.padF = this.padS = 0; this.updateIntent(); }
    if (this.padSprint) { this.padSprint = false; this.updateIntent(); }
    if (this.padGuard) { this.padGuard = false; atlas.guardHeld = false; }
  }

  /** The dash, from a stick FLICK: the stick returning to centre and going out
   *  again in the same direction inside the double-tap window is the pad's
   *  equivalent of tapping a movement key twice. */
  private padDash(_dt: number): void {
    const { lx, ly } = padSnap();
    const mag = Math.hypot(lx, ly);
    const now = performance.now();
    if (mag < PAD_UI_STICK_THRESHOLD * 0.5) { this.padDashDir = ''; return; }   // centred: re-arm
    if (this.padDashDir) return;                                                // already out
    const dir = Math.abs(lx) > Math.abs(ly) ? (lx > 0 ? 'R' : 'L') : (ly > 0 ? 'B' : 'F');
    const dbl = dir === this.padDashLast && now - this.padDashMs < DOUBLE_TAP_MS;
    this.padDashDir = dir;
    this.padDashLast = dir;
    this.padDashMs = dbl ? -Infinity : now;
    if (!dbl) return;
    const yaw = atlas.localYaw;
    const [f, sdir] = DASH_DIRS[dir];
    atlas.intents.request('dash', atlas.fixedTick, -Math.sin(yaw) * f + Math.cos(yaw) * sdir, -Math.cos(yaw) * f - Math.sin(yaw) * sdir);
  }
  private padDashLast = '';

  /** Probe lever: what the pad is, and what it is asking for. */
  private installPadLever(): void {
    (window as unknown as Record<string, unknown>).__gamepad = () => {
      const s = padSnap();
      return {
        supported: typeof (navigator as Navigator & { getGamepads?: unknown }).getGamepads === 'function',
        secureContext: typeof window !== 'undefined' ? window.isSecureContext : null,
        everSeen: padEverSeen(),
        present: s.present, id: s.id, mapping: s.mapping, profile: s.profile,
        lx: s.lx, ly: s.ly, rx: s.rx, ry: s.ry, lt: s.lt, rt: s.rt,
        down: s.down.map((d, i) => (d ? i : -1)).filter((i) => i >= 0),
        uiCapture: atlas.padUiCapture,
        xr: padXrActive(),
        binds: padBindings(),
        capturing: padCaptureActive(),
        lastCapture: padLastCapture(),
        rumble: { on: padRumbleEnabled(), gain: padRumbleGain(), actuator: padHasActuator() },
        foot: { f: this.padF, s: this.padS, sprint: this.padSprint, guard: this.padGuard },
      };
    };
  }

  private updateIntent(): void {
    if (atlas.xrPresenting) return; // the XR locomotion system owns intent in-session
    // Camera-relative on the XZ plane. Forward is -Z rotated by yaw.
    // Keys and the on-screen stick merge (either can drive; both clamp).
    let f = (keyHeldIn(this.keys, 'moveForward') ? 1 : 0) - (keyHeldIn(this.keys, 'moveBack') ? 1 : 0) + this.joyF + this.padF + this.autoF;
    let s = (keyHeldIn(this.keys, 'moveRight') ? 1 : 0) - (keyHeldIn(this.keys, 'moveLeft') ? 1 : 0) + this.joyS + this.padS + this.autoS;
    f = Math.max(-1, Math.min(1, f));
    s = Math.max(-1, Math.min(1, s));

    const yaw = atlas.localYaw;
    // forward = (-sin yaw, -cos yaw); right = (cos yaw, -sin yaw)
    atlas.intentX = -Math.sin(yaw) * f + Math.cos(yaw) * s;
    atlas.intentZ = -Math.cos(yaw) * f - Math.sin(yaw) * s;
    // Movement mode intent (combat wave): sprint while Shift is held with
    // forward intent, else walk. The slide is a latched REQUEST
    // (atlas.slideRequested), not an intent — see the avatar module.
    atlas.intentMode = this.sprinting() && f > 0 ? MODE_SPRINT : MODE_WALK;
  }

  /** Ground combat owns the mouse: not fly/edit/space/vehicle/XR, not loading. */
  /** Probe lever: why the on-foot wheel/keys are (or are not) live right now. */
  private installGateLever(): void {
    (window as unknown as Record<string, unknown>).__inputGate = () => ({
      groundCombat: this.groundCombat(), flyMode: atlas.flyMode, editMode: atlas.editMode, editorCursorMode: atlas.editorCursorMode,
      spaceFlight: atlas.spaceFlight, skyFlight: atlas.skyFlight, hoverRide: atlas.hoverRide, xrPresenting: atlas.xrPresenting, loadingGate: atlas.loadingGate,
      canvasIsApp: this.canvas === document.getElementById('app'), camBack: atlas.camBack,
      captureKeys: this.captureKeys(), fullscreen: !!document.fullscreenElement, keyboardLocked: this.keyboardLocked,
      keyBinds: keyBindings(), keyCapturing: keyCaptureActive(),
      pointerLocked: document.pointerLockElement === this.canvas,
    });
  }

  private groundCombat(): boolean {
    return !atlas.flyMode && !atlas.editMode && !atlas.editorCursorMode && !atlas.spaceFlight
      && !atlas.skyFlight && !atlas.hoverRide && !atlas.xrPresenting && !atlas.loadingGate;
  }

  /** Double-tap detection for the dash: the same movement key pressed twice
   *  inside DOUBLE_TAP_MS files a 'dash' request carrying that key's
   *  camera-relative direction (forward/back/left/right). */
  private lastTapCode = '';
  private lastTapMs = -Infinity;
  private noteTapForDash(code: string): void {
    // Derived from the BOUND movement keys, so a rebound layout still dashes.
    const b = keyBindings();
    const fs: readonly [number, number] | undefined =
      b.moveForward.includes(code) ? [1, 0] : b.moveBack.includes(code) ? [-1, 0]
      : b.moveLeft.includes(code) ? [0, -1] : b.moveRight.includes(code) ? [0, 1] : undefined;
    if (!fs) return;
    const now = performance.now();
    const dbl = code === this.lastTapCode && now - this.lastTapMs < DOUBLE_TAP_MS;
    this.lastTapCode = code;
    this.lastTapMs = dbl ? -Infinity : now; // a triple tap is not two dashes
    if (!dbl) return;
    const yaw = atlas.localYaw;
    const [f, s] = fs;
    atlas.intents.request('dash', atlas.fixedTick, -Math.sin(yaw) * f + Math.cos(yaw) * s, -Math.cos(yaw) * f - Math.sin(yaw) * s);
  }

  private sprinting(): boolean {
    return keyHeldIn(this.keys, 'sprint') || this.padSprint || this.touchSprint;
  }

  private onMouse(e: MouseEvent): void {
    if (atlas.xrPresenting) return; // headset owns the camera pose in-session
    if (atlas.spaceFlight || atlas.skyFlight) return; // Space/AtlasVehicle steer the SHIP with the mouse — stomping
    // cam.rotation.x with the ground FPS pitch here tilted the chase camera off the
    // nose axis (the "ship floats at an oblique angle / feels 2D" report, 2026-07-27)
    if (document.pointerLockElement !== this.canvas) return;
    this.applyLook(e.movementX * MOUSE_SENS, e.movementY * MOUSE_SENS);
  }

  dispose(): void {
    this.keys.clear();
    this.unlockKeyboard();
    this.ac.abort();
    this.touchRoot?.remove();
    this.touchRoot = null;
    this.jumpButton = null;
  }
}
